<template>
  <v-dialog v-model="dialog" persistent max-width="600px">
    <v-card>
      <ModelTitle title="Manage Curriculums" @close="close()" />

      <v-card-text>
        <v-row v-if="selectedClassRoom">
          <v-col cols="12">
            <div>
              Class:
              <span class="font-weight-bold text-capitalize">
                <span
                  v-if="selectedClassRoom.Class && selectedClassRoom.Class.name"
                >
                  {{ selectedClassRoom.Class.name }}
                </span>
                <span v-else>N/A</span>
              </span>
            </div>

            <div>
              Room Name:
              <span class="font-weight-bold text-capitalize">
                <span v-if="selectedClassRoom.name">{{
                  selectedClassRoom.name
                }}</span>
                <span v-else>N/A</span>
              </span>
            </div>
          </v-col>

          <!-- Assigned -->
          <v-col cols="12">
            <div class="mb-2 font-weight-medium">Assigned</div>

            <div v-if="assignedCurriculums.length">
              <v-chip
                v-for="c in assignedCurriculums"
                :key="c.id"
                class="mr-2 mb-2"
                close
                @click:close="removeAssigned(c)"
              >
                {{ c.name }}
              </v-chip>
            </div>

            <div v-else class="grey--text">No curriculums assigned yet.</div>
          </v-col>

          <!-- Select Multiple -->
          <v-col cols="12">
            <v-autocomplete
              dense
              autofocus
              multiple
              chips
              small-chips
              clearable
              :items="Curriculums"
              item-text="name"
              item-value="id"
              v-model="selectedCurriculumIds"
              label="Select Curriculums"
              outlined
              hide-details
              :item-disabled="isCurriculumEmpty"
            >
              <template v-slot:item="{ item }">
                <span>{{ item.name }}</span>
                <v-spacer></v-spacer>

                <v-chip
                  x-small
                  label
                  :color="subjectCount(item) ? 'green' : 'grey'"
                  text-color="white"
                >
                  {{ subjectCount(item) }} subjects
                </v-chip>

                <v-chip
                  v-if="!subjectCount(item)"
                  x-small
                  label
                  class="ml-2"
                  color="red"
                  text-color="white"
                >
                  Empty
                </v-chip>
              </template>

              <template v-slot:selection="{ item }">
                <v-chip small class="mr-1" label>
                  {{ item.name }} ({{ subjectCount(item) }})
                </v-chip>
              </template>
            </v-autocomplete>

            <div class="caption grey--text mt-1">
              Tip: you can pick 1 or more curriculums.
            </div>
          </v-col>
        </v-row>
      </v-card-text>

      <v-card-actions>
        <v-spacer />
        <v-btn color="secondary" text @click="close()">Close</v-btn>
        <v-btn
          color="green lighten-1"
          :loading="loading"
          dark
          @click="submit()"
        >
          Save
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import curriculumService from "@/modules/ReportCard/Curriculums/service.js";
import classRoomService from "../service.js";

export default {
  name: "Assign-Curriculum-To-Classroom",
  props: {
    editDialog: { default: false },
    selectedClassRoom: { type: Object },
  },
  data() {
    return {
      dialog: false,
      loading: false,
      Curriculums: [],
      assignedCurriculums: [],
      selectedCurriculumIds: [],
    };
  },
  watch: {
    editDialog(val) {
      this.dialog = val;
    },
    selectedCurriculumIds(val) {
      if (!Array.isArray(val)) return;

      const validIds = val.filter((id) => {
        const c = (this.Curriculums || []).find((x) => x.id === id);
        return c && this.subjectCount(c) > 0;
      });

      if (validIds.length !== val.length) {
        this.selectedCurriculumIds = validIds;

        this.$swal
          .mixin({
            toast: true,
            position: "top-end",
            showConfirmButton: false,
            timer: 3000,
          })
          .fire({
            icon: "error",
            title: "You can’t assign a curriculum with 0 subjects.",
          });
      }

      // keep chips list in sync (optional)
      this.assignedCurriculums = (this.Curriculums || []).filter((x) =>
        validIds.includes(x.id)
      );
    },
    async dialog(val) {
      if (!val) return;

      await this.getCurriculums();

      var assigned = Array.isArray(
        this.selectedClassRoom && this.selectedClassRoom.Curriculums
      )
        ? this.selectedClassRoom.Curriculums
        : [];

      this.assignedCurriculums = assigned;
      this.selectedCurriculumIds = assigned.map(function (x) {
        return x.id;
      });
    },
  },
  methods: {
    isCurriculumEmpty(item) {
      return this.subjectCount(item) === 0;
    },
    subjectCount(curr) {
      if (!curr) return 0;

      const meta = curr.subjectMeta;

      // ✅ your current structure: subjectMeta.subjects = []
      if (meta && Array.isArray(meta.subjects)) {
        return meta.subjects.length;
      }

      // fallback: subjectMeta is an array itself
      if (Array.isArray(meta)) return meta.length;

      // fallback: subjectMeta is an object-map of subjects
      if (meta && typeof meta === "object") {
        // if it’s like { subjects: {...} }
        if (
          meta.subjects &&
          typeof meta.subjects === "object" &&
          !Array.isArray(meta.subjects)
        ) {
          return Object.keys(meta.subjects).length;
        }
        return Object.keys(meta).length;
      }

      return 0;
    },
    removeAssigned(c) {
      this.selectedCurriculumIds = (this.selectedCurriculumIds || []).filter(
        function (id) {
          return id !== c.id;
        }
      );

      this.assignedCurriculums = (this.assignedCurriculums || []).filter(
        function (x) {
          return x.id !== c.id;
        }
      );
    },

    async submit() {
      try {
        this.loading = true;

        // NEW endpoint you add on backend
        await classRoomService.setCurriculums(this.selectedClassRoom.id, {
          CurriculumIds: this.selectedCurriculumIds || [],
        });

        this.$swal
          .mixin({
            toast: true,
            position: "top-end",
            showConfirmButton: false,
            timer: 3000,
          })
          .fire({
            icon: "success",
            title: "Curriculums updated",
          });

        this.empty();
        this.$emit("submit");
      } catch (err) {
        console.log('err', err)
        this.$swal
          .mixin({
            toast: true,
            position: "top-end",
            showConfirmButton: false,
            timer: 3000,
          })
          .fire({
            icon: "error",
            title:
              err && err.data && err.data.message
                ? err.data.message
                : "Something went wrong",
          });
      } finally {
        this.loading = false;
      }
    },

    getCurriculums() {
      console.log("selectedClassRoom", this.selectedClassRoom);
      return curriculumService.getAll().then((response) => {
        console.log("response getCurriculums:", response);
        this.Curriculums = response.data || [];
        return response;
      });
    },

    close() {
      this.empty();
      this.$emit("close");
    },

    empty() {
      this.selectedCurriculumIds = [];
      this.assignedCurriculums = [];
    },
  },
};
</script>
